import { useState, useMemo } from "react";
import { useGetList } from "ra-core";
import {
  Check,
  ChevronsUpDown,
  FileText,
  DollarSign,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Proposal } from "../../providers/fakerest/dataGenerator/types";

interface ProposalSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  jobId?: string;
  placeholder?: string;
  disabled?: boolean;
  onCreateNew?: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const statusColors: Record<string, string> = {
  draft: "text-gray-500",
  sent: "text-blue-500",
  accepted: "text-green-500",
  declined: "text-red-500",
};

export const ProposalSearchSelect = ({
  value,
  onChange,
  jobId,
  placeholder = "Select a proposal...",
  disabled = false,
  onCreateNew,
}: ProposalSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch proposals filtered by job if provided
  const { data: proposals } = useGetList<Proposal>("proposals", {
    pagination: { page: 1, perPage: 500 },
    filter: jobId ? { job_id: Number(jobId) } : {},
    sort: { field: "created_at", order: "DESC" },
  });

  // Filter proposals based on search
  const filteredProposals = useMemo(() => {
    if (!proposals) return [];
    if (!search) return proposals;

    const searchLower = search.toLowerCase();
    return proposals.filter(
      (proposal) =>
        String(proposal.id).includes(searchLower) ||
        proposal.status.toLowerCase().includes(searchLower) ||
        formatCurrency(proposal.total).toLowerCase().includes(searchLower),
    );
  }, [proposals, search]);

  // Find selected proposal
  const selectedProposal = useMemo(() => {
    if (!value || !proposals) return null;
    return proposals.find((proposal) => String(proposal.id) === value);
  }, [value, proposals]);

  const handleSelect = (proposalId: string) => {
    onChange(proposalId);
    setOpen(false);
    setSearch("");
  };

  const handleCreateNew = () => {
    setOpen(false);
    setSearch("");
    onCreateNew?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 py-2 font-normal"
          disabled={disabled}
        >
          {selectedProposal ? (
            <div className="flex items-center gap-2 text-left">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>#{selectedProposal.id}</span>
              <span className="text-muted-foreground">-</span>
              <span className="font-medium">
                {formatCurrency(selectedProposal.total)}
              </span>
              <span
                className={`text-sm ${statusColors[selectedProposal.status]}`}
              >
                ({selectedProposal.status})
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground">
              {disabled && !jobId ? "Select a job first" : placeholder}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by ID, status, or amount..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {onCreateNew && (
              <CommandGroup>
                <CommandItem
                  onSelect={handleCreateNew}
                  className="py-2 text-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create new proposal
                </CommandItem>
              </CommandGroup>
            )}
            {onCreateNew && <CommandSeparator />}
            <CommandEmpty>No proposals found.</CommandEmpty>
            <CommandGroup>
              {filteredProposals.map((proposal) => (
                <CommandItem
                  key={proposal.id}
                  value={String(proposal.id)}
                  onSelect={() => handleSelect(String(proposal.id))}
                  className="py-2"
                >
                  {value === String(proposal.id) && (
                    <Check className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>#{proposal.id}</span>
                      <span className="text-muted-foreground">-</span>
                      <span className="font-medium">
                        {formatCurrency(proposal.total)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className={statusColors[proposal.status]}>
                        {proposal.status}
                      </span>
                      <span>•</span>
                      <span>{proposal.line_items?.length || 0} items</span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
